if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: null,
          viewer: null,
          thumbnails: null,
        };

        this.refreshElements();

        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer) return;

        this.dots = this.querySelector('.media-gallery-dots');
        this.mediaItems = this.elements.viewer.querySelectorAll('[data-media-id]');

        if (this.dots && this.mediaItems.length) {
          this.createDots();
        }

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              mediaToSwitch
                .querySelector('button')
                .addEventListener(
                  'click',
                  this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
                );
            });
        }

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      refreshElements() {
        this.elements.liveRegion = this.querySelector('[id^="GalleryStatus"]');
        this.elements.viewer = this.querySelector('[id^="GalleryViewer"]');
        this.elements.thumbnails = this.querySelector('[id^="GalleryThumbnails"]');
      }

      /* ================= SLIDE CHANGE ================= */
      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }

        this.updateDots(mediaId);
      }

      /* ================= SET ACTIVE MEDIA ================= */
      setActiveMedia(mediaId, prepend) {
        this.refreshElements();
        if (!this.elements.viewer) return;

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        const activeMediaParent = activeMedia.parentElement;
        if (!activeMediaParent) return;

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((el) => el.classList.remove('is-active'));

        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMediaParent.firstChild !== activeMedia &&
            activeMediaParent.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            if (activeThumbnail?.parentElement) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMediaParent.scrollTo({ left: activeMedia.offsetLeft });
          }

          const rect = activeMedia.getBoundingClientRect();
          if (rect.top <= -0.5) {
            window.scrollTo({
              top: rect.top + window.scrollY,
              behavior: 'smooth'
            });
          }
        });

        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }

        /* -------- UPDATE DOTS -------- */
        this.updateDots(mediaId);
      }

      /* ================= THUMBNAILS ================= */
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((el) => el.removeAttribute('aria-current'));

        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft
        });
      }

      /* ================= DOTS METHODS ================= */
      createDots() {
        this.dots.innerHTML = '';

        this.mediaItems.forEach((media, index) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.setAttribute('aria-label', 'Go to media ' + (index + 1));

          if (media.classList.contains('is-active')) {
            btn.classList.add('is-active');
          }

          btn.addEventListener('click', () => {
            this.setActiveMedia(media.dataset.mediaId, false);
          });

          this.dots.appendChild(btn);
        });
      }

      updateDots(activeMediaId) {
        if (!this.dots) return;

        [...this.dots.children].forEach((dot, index) => {
          const media = this.mediaItems[index];
          dot.classList.toggle(
            'is-active',
            media.dataset.mediaId === activeMediaId
          );
        });
      }

      /* ================= ACCESSIBILITY ================= */
      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      /* ================= MEDIA ================= */
      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      /* ================= STICKY HEADER ================= */
      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      /* ================= SEMANTICS ================= */
      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );
}
